import { useEffect, useState } from 'react';

/**
 * Resolves the current desktop release: version, and a download URL per
 * platform.
 *
 * Assets come from the GitHub Releases API at call time rather than
 * being hardcoded, so cutting a release needs no frontend deploy. The
 * source repository is private; only `co-pilot-releases` (binaries,
 * public) is queried here, and it is public precisely so this call
 * needs no token - see docs/desktop-distribution.md.
 *
 * Rate limiting is not a concern: GitHub's unauthenticated limit is per
 * ORIGINATING IP, and each visitor's browser makes its own request, so
 * the budget is 60/hour per visitor rather than 60/hour in total.
 *
 * This lives in a hook, not in the page, because two surfaces show the
 * same downloads - the public /download page and the in-app modal - and
 * a copy in each is a copy that eventually disagrees about which asset
 * is the right one.
 */
const RELEASES_REPO = 'krishvatech/co-pilot-releases';
const RELEASES_API = `https://api.github.com/repos/${RELEASES_REPO}/releases/latest`;

export const RELEASES_PAGE = `https://github.com/${RELEASES_REPO}/releases/latest`;

// What each build runs on, shown under the download buttons. Only claims
// the code and release pipeline back up - do not widen these because a new
// OS version shipped, only after it has been verified with the app:
//
//   Windows - 10 and 11, 64-bit only. Below build 19041 (version 2004)
//     the overlay is hidden only from window and tab shares; an
//     entire-screen share shows a dark box (desktop/electron/commands/
//     protection.ts, CaptureScope). DesktopDownload warns those visitors
//     before the download starts. CI builds a single x64 installer
//     (.github/workflows/desktop-release.yml, `windows-latest`, no ARM64
//     target; latest.json carries only `windows-x86_64`).
//   macOS - 14.6 or later, Apple Silicon and Intel.
//     audio/capture/platform/macos.rs reports other applications' audio
//     (the interviewer's voice) unsupported below 14.6, cpal 0.18's floor
//     for its Core Audio process tap. The bundle itself would launch on an
//     older Mac (Tauri's default LSMinimumSystemVersion is 10.13; the app
//     does not override it), where only the microphone works. One universal
//     .dmg is built (`--target universal-apple-darwin`).
const WINDOWS_REQUIREMENTS = ['Windows 10 or 11', '64-bit (x64)'];
const MACOS_REQUIREMENTS = ['macOS 14.6 or later', 'Apple Silicon & Intel', 'Protected overlay and meetings currently unavailable'];
const UBUNTU_REQUIREMENTS = ['Ubuntu beta · X11 or XWayland', '64-bit (x86_64) · .deb installer', 'Protected overlay and meetings currently unavailable'];

export const PLATFORMS = [
  {
    id: 'windows',
    label: 'Windows',
    shortLabel: 'Windows',
    icon: 'windows',
    // The NSIS installer, not the MSI: it is the artifact the updater
    // ships, so a customer who installs it can actually auto-update.
    // The MSI is offered separately for managed/enterprise installs.
    // Tauri names it `KrishvaQ_1.0.13_x64-setup.exe`; the Electron app
    // (2.x) `KrishvaQ-Setup-2.0.0.exe` - both are accepted, never a
    // `.exe.blockmap` or `.exe.sig` next to it.
    match: (name) => name.endsWith('-setup.exe') || /^KrishvaQ-Setup-[^/\\]+\.exe$/.test(name),
    requirements: WINDOWS_REQUIREMENTS,
    note: WINDOWS_REQUIREMENTS.join(' \u00b7 '),
  },
  {
    id: 'macos',
    label: 'macOS',
    shortLabel: 'macOS',
    icon: 'apple',
    // One universal .dmg for every Mac - Apple Silicon and Intel alike -
    // so a visitor never has to pick an architecture (and never risks
    // getting the wrong one). The CI workflow also still publishes
    // architecture-specific .dmg files for the Tauri updater's own use;
    // this intentionally does NOT match those; see
    // .github/workflows/desktop-release.yml.
    match: (name) => name.endsWith('.dmg') && /universal/i.test(name),
    requirements: MACOS_REQUIREMENTS,
    note: MACOS_REQUIREMENTS.join(' \u00b7 '),
  },
  {
    id: 'linux',
    label: 'Ubuntu',
    shortLabel: 'Ubuntu',
    icon: 'download',
    match: (name) => /^KrishvaQ-[^/\\]+-linux-amd64\.deb$/.test(name),
    requirements: UBUNTU_REQUIREMENTS,
    note: UBUNTU_REQUIREMENTS.join(' · '),
  },
];

/**
 * Best guess at the visitor's platform, used only to decide which
 * download is promoted - every platform stays reachable, because a
 * wrong guess must never hide the one someone actually needs.
 */
function detectPlatform() {
  const ua = navigator.userAgent;

  if (/Windows/i.test(ua)) return 'windows';

  if (/Mac OS X|Macintosh/i.test(ua)) {
    return 'macos';
  }

  // Android also includes Linux in its user agent; it cannot install DEB.
  if (/Linux|Ubuntu/i.test(ua) && !/Android/i.test(ua)) return 'linux';

  return null;
}

export function useDesktopRelease() {
  const [assets, setAssets] = useState(null);
  const [version, setVersion] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch(RELEASES_API, { headers: { Accept: 'application/vnd.github+json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`GitHub returned ${response.status}`);
        return response.json();
      })
      .then((release) => {
        if (cancelled) return;
        setAssets(release.assets || []);
        setVersion((release.tag_name || '').replace(/^desktop-v/, ''));
      })
      .catch(() => {
        // Never leaves the visitor stranded: the releases page is always
        // linked, so a failed call degrades to one extra click rather
        // than no download at all.
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const urlFor = (platform) => {
    if (!assets) return null;
    const asset = assets.find((a) => platform.match(a.name));
    return asset ? asset.browser_download_url : null;
  };

  const detected = detectPlatform();
  const ordered = detected
    ? [...PLATFORMS].sort((a, b) => (a.id === detected ? -1 : b.id === detected ? 1 : 0))
    : PLATFORMS;

  const [primary, ...others] = ordered;

  return {
    version,
    failed,
    /** True until the release list has either arrived or failed. */
    loading: !assets && !failed,
    primary,
    primaryUrl: urlFor(primary),
    // Do not advertise an Ubuntu download before a release actually has it.
    others: others.map((platform) => ({ ...platform, url: urlFor(platform) }))
      .filter((platform) => platform.id !== 'linux' || platform.url),
    releasesPage: RELEASES_PAGE,
  };
}

export default useDesktopRelease;
